import * as React from "react";
import { Label } from "./label";
import { Input, type InputProps } from "./input";
import { PasswordInput } from "@/components/shared/password-input";
import { cn } from "@/lib/utils";

interface FormFieldProps extends Omit<InputProps, "onChange"> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  containerClassName?: string;
}

/**
 * Composed label + input + error + hint primitive. Used across
 * the auth wizard, Settings forms, and BYOK dialogs so spacing,
 * alignment, and error styling stay consistent.
 *
 * When ``type="password"`` the input slot renders ``PasswordInput``
 * instead of a plain ``<Input>``, so every password field across
 * the app picks up the eye-toggle reveal affordance without a
 * per-call-site migration.
 */
export function FormField({
  label,
  value,
  onChange,
  error,
  hint,
  containerClassName,
  id,
  type,
  ...inputProps
}: FormFieldProps) {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const isPassword = type === "password";
  const sharedProps = {
    id: fieldId,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    "aria-invalid": (error ? "true" : "false") as "true" | "false",
    "aria-describedby": error
      ? `${fieldId}-error`
      : hint
        ? `${fieldId}-hint`
        : undefined,
    ...inputProps,
  };
  return (
    <div className={cn("space-y-1.5", containerClassName)}>
      <Label htmlFor={fieldId}>{label}</Label>
      {isPassword ? (
        <PasswordInput {...sharedProps} />
      ) : (
        <Input {...sharedProps} type={type} />
      )}
      {hint && !error && (
        <p id={`${fieldId}-hint`} className="text-xs text-neutral-500">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${fieldId}-error`}
          className="text-xs text-danger-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
